import { inject } from '@angular/core';
import { HttpInterceptorFn } from '@angular/common/http';
import { KeycloakService } from 'keycloak-angular';
import { from, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

const isBearerTokenEnabled = (): boolean => {
  const flag = (environment as any).bearer_token;
  if (typeof flag === 'boolean') {
    return flag;
  }
  return String(flag ?? '').trim().toLowerCase() !== 'false';
};

export const authHeadersInterceptor: HttpInterceptorFn = (req, next) => {
  const keycloak = inject(KeycloakService);
  const isAssetRequest = req.url.startsWith('/assets') || req.url.startsWith('assets/');

  const baseHeaders: Record<string, string> = {
    ...(req.headers.has('Content-Type') ? {} : { 'Content-Type': 'application/json' }),
    ...(req.headers.has('Accept') ? {} : { Accept: 'application/json' }),
  };

  if (isAssetRequest) {
    return next(req.clone({ setHeaders: baseHeaders }));
  }

  if (!isBearerTokenEnabled()) {
    const headers = req.headers.delete('Authorization').delete('authorization');
    return next(req.clone({ headers, setHeaders: baseHeaders }));
  }

  if (req.headers.has('Authorization') || req.headers.has('authorization')) {
    return next(req.clone({ setHeaders: baseHeaders }));
  }

  // getToken() refreshes the token first when it's expired or close to
  // expiring, so every outgoing request carries a currently-valid bearer
  // token. Reading keycloak.getKeycloakInstance().token directly (as this
  // interceptor did before) returns whatever is cached, which goes stale
  // for any request fired after the token's lifetime has elapsed since
  // login/last refresh (e.g. submitting after editing for a while) —
  // silently sending an expired token that the backend rejects.
  //
  // Deliberately NOT gating this on keycloak.isLoggedIn() first: that flag
  // reflects keycloak-js's last-known internal state, which can go stale
  // for the exact same reason the cached token above did (e.g. a request
  // fired well after the last background refresh check). Calling
  // getToken() unconditionally lets Keycloak do the real, live
  // validity/refresh check instead of trusting a snapshot that may say
  // "not logged in" for a session that's actually still refreshable —
  // which is what caused this request's Authorization header to be
  // dropped entirely instead of just being stale.
  return from(keycloak.getToken()).pipe(
    catchError(() => of<string | null>(null)),
    switchMap((token) => {
      const headers = {
        ...baseHeaders,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      return next(req.clone({ setHeaders: headers }));
    }),
  );
};
